import React, { useState, useEffect } from 'react'
import GoogleLoginButton from 'components/shared/GoogleLoginButton'
import { isInAppBrowser, openInExternalBrowser } from 'utils/inAppBrowserUtils'
import { checkEmail, registerUser, loginUser } from 'utils/teachingTeamApi'
import { trackEvent } from 'utils/analytics'

export default function LoginModal({ isOpen, onClose, teamLogo, teamName, colors, translations, redirectUrl, startOnEmail = false, intent }) {
  const [email, setEmail] = useState('')
  const [emailStatus, setEmailStatus] = useState(null)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  // startOnEmail skips the chooser. A caller whose own page already offers both
  // buttons would otherwise reopen the same choice inside the modal.
  const [showEmailFlow, setShowEmailFlow] = useState(startOnEmail)

  useEffect(() => {
    if (isOpen) {
      trackEvent('login_modal_open', { team_name: teamName })
      setShowEmailFlow(startOnEmail)
    }
  }, [isOpen, startOnEmail])

  if (!isOpen) return null

  const actualRedirectUrl = redirectUrl || window.location.pathname + window.location.search
  const t = translations?.loginModal || {}

  // Six places open this dialog for five different actions. Describe the one
  // the reader started, so a free action is never introduced as a purchase.
  const PROMPT_BY_INTENT = {
    purchase: t.subtitle,
    trial: t.subtitleTrial,
    group_class: t.subtitleGroupClass,
    follow: t.subtitleFollow,
    coupon: t.subtitleCoupon
  }
  const prompt = PROMPT_BY_INTENT[intent] || t.subtitle || 'Log in to purchase courses'
  const inAppT = translations?.inAppBrowser || {}
  const inApp = isInAppBrowser()

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose()
  }

  const handleCheckEmail = async (e) => {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true)
    setError(null)
    try {
      const data = await checkEmail(email.trim())
      setEmailStatus(data.status)
    } catch (err) {
      setError(err.message || t.error || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      await registerUser(email.trim(), name.trim(), password)
      trackEvent('login_completed', { method: 'email_register', team_name: teamName })
      window.location.reload()
    } catch (err) {
      setError(err.message || t.error || 'Something went wrong')
      setLoading(false)
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      await loginUser(email.trim(), password)
      trackEvent('login_completed', { method: 'email_login', team_name: teamName })
      window.location.reload()
    } catch (err) {
      setError(err.message || t.login_failed || 'Login failed')
      setLoading(false)
    }
  }

  const handleBackToEmail = () => {
    setEmailStatus(null)
    setPassword('')
    setName('')
    setError(null)
    if (!inApp) setShowEmailFlow(false)
  }

  const handleOpenInBrowser = () => {
    openInExternalBrowser(window.location.href)
  }

  const handleCopyUrl = () => {
    navigator.clipboard?.writeText(window.location.href)
  }

  // Render in-app browser email flow
  const renderInAppContent = () => {
    // State: email input
    if (!emailStatus) {
      return (
        <form onSubmit={handleCheckEmail}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.email_placeholder || 'your@email.com'}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              autoFocus
            />
          </div>
          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
          <button
            type="submit"
            disabled={loading || !email.trim()}
            className="w-full py-3 rounded-xl text-white font-medium text-sm transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#3366FF' }}
          >
            {loading ? (t.checking || 'Checking...') : (t.continue_with_email || 'Continue with Email')}
          </button>
        </form>
      )
    }

    // State: register (new email)
    if (emailStatus === 'new') {
      return (
        <form onSubmit={handleRegister}>
          <p className="text-sm text-gray-500 mb-3">{email}</p>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.name_label || 'Name'}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.name_placeholder || 'How should we call you?'}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              autoFocus
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.password_label || 'Password'}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t.password_placeholder || 'At least 6 characters'}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <p className="text-xs text-gray-400 mt-1">{t.password_min_length || 'At least 6 characters'}</p>
          </div>
          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
          <button
            type="submit"
            disabled={loading || !name.trim() || password.length < 6}
            className="w-full py-3 rounded-xl text-white font-medium text-sm transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#3366FF' }}
          >
            {loading ? (t.registering || 'Creating account...') : (t.register_button || 'Sign up')}
          </button>
          <button type="button" onClick={handleBackToEmail} className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer">
            {t.back_to_email || 'Use a different email'}
          </button>
        </form>
      )
    }

    // State: login (existing password account)
    if (emailStatus === 'password') {
      return (
        <form onSubmit={handleLogin}>
          <p className="text-sm text-gray-500 mb-3">{email}</p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.password_label || 'Password'}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t.password_placeholder_login || 'Enter your password'}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              autoFocus
            />
          </div>
          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password}
            className="w-full py-3 rounded-xl text-white font-medium text-sm transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#3366FF' }}
          >
            {loading ? (t.logging_in || 'Logging in...') : (t.login_button || 'Log in')}
          </button>
          <div className="flex justify-between mt-3">
            <button type="button" onClick={handleBackToEmail} className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer">
              {t.back_to_email || 'Use a different email'}
            </button>
            <a href="/users/password/new" className="text-sm text-blue-500 hover:text-blue-700">
              {t.forgot_password || 'Forgot password?'}
            </a>
          </div>
        </form>
      )
    }

    // State: oauth_only
    if (emailStatus === 'oauth_only') {
      return (
        <div className="text-center">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-amber-800">
              {t.oauth_only_message || 'This account was created with Google. Please open this page in Safari or Chrome to log in.'}
            </p>
          </div>
          <button
            onClick={handleOpenInBrowser}
            className="w-full py-3 rounded-xl text-white font-medium text-sm mb-2"
            style={{ backgroundColor: '#3366FF' }}
          >
            {inAppT.openInBrowser || t.open_in_browser || 'Open in Browser'}
          </button>
          <button
            onClick={handleCopyUrl}
            className="w-full py-2 text-sm text-blue-500 hover:text-blue-700 cursor-pointer"
          >
            {inAppT.copyUrl || t.copy_url || 'Copy URL'}
          </button>
          <button type="button" onClick={handleBackToEmail} className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer">
            {t.back_to_email || 'Use a different email'}
          </button>
        </div>
      )
    }

    return null
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl max-w-md w-full relative">
        {/* Header */}
        <div className="flex items-center justify-center p-4 border-b border-gray-200 relative">
          <button
            onClick={onClose}
            className="absolute left-4 p-1 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="font-semibold" style={{ color: '#222222' }}>{t.title || 'Log in or Sign up'}</span>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Team Logo */}
          <div className="flex justify-center mb-6">
            {teamLogo ? (
              <img src={teamLogo} alt={teamName || 'Team'} className="w-16 h-16 rounded-xl object-cover" />
            ) : (
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
                style={{ background: colors?.primaryGradient || 'linear-gradient(to right, #3366FF, #5588FF)' }}
              >
                {teamName?.charAt(0) || 'T'}
              </div>
            )}
          </div>

          {/* Welcome text */}
          <h2 className="text-xl font-semibold text-center mb-2" style={{ color: '#222222' }}>
            {(t.welcome || 'Welcome to %{team_name}').replace('%{team_name}', teamName || 'the Course Platform')}
          </h2>
          <p className="text-center mb-6" style={{ color: '#717171' }}>
            {prompt}
          </p>

          {/* Auth options */}
          {inApp ? renderInAppContent() : showEmailFlow ? (
            <>
              {renderInAppContent()}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-gray-400">{t.or_divider || 'or'}</span></div>
              </div>
              <GoogleLoginButton
                role="student"
                label={t.googleButton || 'Continue with Google'}
                extraParams={{ redirect_url: actualRedirectUrl }}
                translations={inAppT}
                className="py-3.5 border-2 rounded-xl justify-center gap-3"
              />
            </>
          ) : (
            <>
              <GoogleLoginButton
                role="student"
                label={t.googleButton || 'Continue with Google'}
                extraParams={{ redirect_url: actualRedirectUrl }}
                translations={inAppT}
                className="py-3.5 border-2 rounded-xl justify-center gap-3"
              />
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-gray-400">{t.or_divider || 'or'}</span></div>
              </div>
              <button
                onClick={() => setShowEmailFlow(true)}
                className="w-full py-3 rounded-xl font-medium text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                {t.continue_with_email || 'Continue with Email'}
              </button>
            </>
          )}

          {/* Terms */}
          <p className="text-xs text-center mt-6" style={{ color: '#717171' }}>
            {t.terms_consent || 'By signing up, you agree to our'}{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 underline">
              {t.terms_link || 'Terms of Service'}
            </a>
            {' '}{t.terms_consent_and || 'and'}{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 underline">
              {t.privacy_link || 'Privacy Policy'}
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
